/**
 * CheckpointSystem - Manages spawn points, checkpoints, and player respawning
 * Handles checkpoint activation, respawn logic, and spawn point management
 */

import type { SpawnPoint } from '../entities/SpawnPoint';
import type { Player } from '../entities/Player';
import type { Vector2 } from '../../types/game';

export interface RespawnData {
  position: Vector2;
  facingDirection: 'left' | 'right';
  checkpointName: string;
  timestamp: number;
}

export class CheckpointSystem {
  private static instance: CheckpointSystem;

  // Spawn point management
  private playerSpawns: Map<string, SpawnPoint> = new Map();
  private enemySpawns: Map<string, SpawnPoint> = new Map();
  private checkpoints: Map<string, SpawnPoint> = new Map();

  // Active checkpoint tracking
  private activeCheckpoint: SpawnPoint | null = null;
  private defaultSpawn: SpawnPoint | null = null;

  // Respawn tracking
  private lastRespawnData: RespawnData | null = null;
  private checkpointRadius: number = 50; // pixels

  // Callbacks
  private onCheckpointActivated: ((checkpoint: SpawnPoint) => void) | null = null;
  private onPlayerRespawned: ((respawnData: RespawnData) => void) | null = null;

  private constructor() {}

  public static getInstance(): CheckpointSystem {
    if (!CheckpointSystem.instance) {
      CheckpointSystem.instance = new CheckpointSystem();
    }
    return CheckpointSystem.instance;
  }

  /**
   * Register a spawn point
   */
  public registerSpawnPoint(spawnPoint: SpawnPoint): void {
    const type = spawnPoint.getSpawnType();

    switch (type) {
      case 'player':
        this.playerSpawns.set(spawnPoint.id, spawnPoint);
        if (spawnPoint.getIsDefault() && !this.defaultSpawn) {
          this.defaultSpawn = spawnPoint;
        }
        console.log(`📍 Registered player spawn: ${spawnPoint.id}`);
        break;

      case 'enemy':
        this.enemySpawns.set(spawnPoint.id, spawnPoint);
        console.log(`📍 Registered enemy spawn: ${spawnPoint.id}`);
        break;

      case 'checkpoint':
        this.checkpoints.set(spawnPoint.id, spawnPoint);
        console.log(`📍 Registered checkpoint: ${spawnPoint.id}`);
        break;
    }
  }

  /**
   * Unregister a spawn point
   */
  public unregisterSpawnPoint(spawnPointId: string): void {
    this.playerSpawns.delete(spawnPointId);
    this.enemySpawns.delete(spawnPointId);
    this.checkpoints.delete(spawnPointId);
    console.log(`📍 Unregistered spawn point: ${spawnPointId}`);
  }

  /**
   * Update checkpoint system (check for checkpoint activation)
   */
  public update(player: Player): void {
    // Check if player is near any checkpoint
    this.checkpoints.forEach(checkpoint => {
      if (checkpoint.getIsActivated()) {
        return; // Skip already activated checkpoints
      }

      const distance = this.calculateDistance(player.position, checkpoint.position);
      if (distance <= this.checkpointRadius) {
        this.activateCheckpoint(checkpoint);
      }
    });
  }

  /**
   * Activate a checkpoint
   */
  public activateCheckpoint(checkpoint: SpawnPoint): void {
    console.log(`🎯 CheckpointSystem: Activating checkpoint ${checkpoint.getCheckpointName()}`);
    console.log(`   Checkpoint ID: ${checkpoint.id}`);
    console.log(`   Checkpoint position: (${checkpoint.position.x}, ${checkpoint.position.y})`);

    // Deactivate previous checkpoint
    if (this.activeCheckpoint && this.activeCheckpoint.id !== checkpoint.id) {
      console.log(`   Deactivating previous checkpoint: ${this.activeCheckpoint.getCheckpointName()}`);
      this.activeCheckpoint.deactivate();
    }

    // Activate new checkpoint
    checkpoint.activate();
    this.activeCheckpoint = checkpoint;
    console.log(`   ✅ Set as active checkpoint`);

    // Update respawn data
    const spawnInfo = checkpoint.getSpawnInfo();
    this.lastRespawnData = {
      position: spawnInfo.position,
      facingDirection: spawnInfo.facingDirection,
      checkpointName: checkpoint.getCheckpointName(),
      timestamp: Date.now(),
    };
    console.log(`   Respawn data saved: (${spawnInfo.position.x}, ${spawnInfo.position.y})`);

    console.log(`✅ Checkpoint activated: ${checkpoint.getCheckpointName()}`);

    // Trigger callback
    this.onCheckpointActivated?.(checkpoint);
  }

  /**
   * Get respawn position and data
   */
  public getRespawnData(): RespawnData {
    // Use active checkpoint if available
    if (this.activeCheckpoint) {
      const spawnInfo = this.activeCheckpoint.getSpawnInfo();
      return {
        position: spawnInfo.position,
        facingDirection: spawnInfo.facingDirection,
        checkpointName: this.activeCheckpoint.getCheckpointName(),
        timestamp: this.activeCheckpoint.getActivationTime(),
      };
    }

    // Use default spawn if available
    if (this.defaultSpawn) {
      const spawnInfo = this.defaultSpawn.getSpawnInfo();
      return {
        position: spawnInfo.position,
        facingDirection: spawnInfo.facingDirection,
        checkpointName: this.defaultSpawn.getCheckpointName(),
        timestamp: 0,
      };
    }

    // Fallback to origin
    return {
      position: { x: 0, y: 0 },
      facingDirection: 'right',
      checkpointName: 'Default',
      timestamp: 0,
    };
  }

  /**
   * Respawn the player at the current checkpoint
   */
  public respawnPlayer(player: Player): void {
    console.log(`🔄 CheckpointSystem: Respawning player...`);
    console.log(`   Active checkpoint: ${this.activeCheckpoint?.getCheckpointName() || 'NONE'}`);

    const respawnData = this.getRespawnData();
    console.log(`   Respawn data position: (${respawnData.position.x}, ${respawnData.position.y})`);
    console.log(`   Player position BEFORE: (${player.position.x}, ${player.position.y})`);

    // Set player position
    player.position.x = respawnData.position.x;
    player.position.y = respawnData.position.y;

    console.log(`   Player position AFTER: (${player.position.x}, ${player.position.y})`);

    // Reset player velocity
    player.velocity.x = 0;
    player.velocity.y = 0;

    console.log(`✅ Player respawned at: ${respawnData.checkpointName}`);

    // Trigger callback
    this.onPlayerRespawned?.(respawnData);
  }

  /**
   * Get a random enemy spawn point
   */
  public getRandomEnemySpawn(): SpawnPoint | null {
    const spawns = Array.from(this.enemySpawns.values());
    if (spawns.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * spawns.length);
    return spawns[randomIndex] ?? null;
  }

  /**
   * Get all enemy spawn points
   */
  public getEnemySpawns(): SpawnPoint[] {
    return Array.from(this.enemySpawns.values());
  }

  /**
   * Get all player spawn points
   */
  public getPlayerSpawns(): SpawnPoint[] {
    return Array.from(this.playerSpawns.values());
  }

  /**
   * Get all checkpoints
   */
  public getCheckpoints(): SpawnPoint[] {
    return Array.from(this.checkpoints.values());
  }

  /**
   * Get active checkpoint
   */
  public getActiveCheckpoint(): SpawnPoint | null {
    return this.activeCheckpoint;
  }

  /**
   * Get default spawn point
   */
  public getDefaultSpawn(): SpawnPoint | null {
    return this.defaultSpawn;
  }

  /**
   * Set checkpoint activation radius
   */
  public setCheckpointRadius(radius: number): void {
    this.checkpointRadius = radius;
  }

  /**
   * Calculate distance between two positions
   */
  private calculateDistance(pos1: Vector2, pos2: Vector2): number {
    const dx = pos2.x - pos1.x;
    const dy = pos2.y - pos1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Set callback for checkpoint activation
   */
  public setOnCheckpointActivated(callback: (checkpoint: SpawnPoint) => void): void {
    this.onCheckpointActivated = callback;
  }

  /**
   * Set callback for player respawn
   */
  public setOnPlayerRespawned(callback: (respawnData: RespawnData) => void): void {
    this.onPlayerRespawned = callback;
  }

  /**
   * Reset all checkpoints
   */
  public resetCheckpoints(): void {
    this.checkpoints.forEach(checkpoint => checkpoint.deactivate());
    this.activeCheckpoint = null;
    this.lastRespawnData = null;
    console.log('🔄 All checkpoints reset');
  }

  /**
   * Clear all spawn points
   */
  public clear(): void {
    this.playerSpawns.clear();
    this.enemySpawns.clear();
    this.checkpoints.clear();
    this.activeCheckpoint = null;
    this.defaultSpawn = null;
    this.lastRespawnData = null;
    console.log('📍 Checkpoint system cleared');
  }

  /**
   * Get system statistics
   */
  public getStats(): {
    playerSpawns: number;
    enemySpawns: number;
    checkpoints: number;
    activeCheckpoint: string | null;
    defaultSpawn: string | null;
  } {
    return {
      playerSpawns: this.playerSpawns.size,
      enemySpawns: this.enemySpawns.size,
      checkpoints: this.checkpoints.size,
      activeCheckpoint: this.activeCheckpoint?.id || null,
      defaultSpawn: this.defaultSpawn?.id || null,
    };
  }
}

// Export singleton instance
export const checkpointSystem = CheckpointSystem.getInstance();
